/**
 * Client-side validation utilities
 * Provides instant feedback before server submission
 */

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Email validation
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * UK phone number validation (flexible format)
 */
export const isValidUKPhone = (phone: string): boolean => {
  if (!phone) return false;
  // Remove spaces, dashes, and parentheses
  const cleaned = phone.replace(/[\s\-()]/g, '');
  // UK numbers are typically 10-11 digits, optionally starting with +44
  const phoneRegex = /^(\+44|0)?[0-9]{10,11}$/;
  return phoneRegex.test(cleaned);
};

/**
 * UK postcode validation
 */
export const isValidUKPostcode = (postcode: string): boolean => {
  if (!postcode) return false;
  const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i;
  return postcodeRegex.test(postcode.trim());
};

/**
 * Positive number validation
 */
export const isPositiveNumber = (value: any): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

/**
 * Non-negative number validation (allows 0)
 */
export const isNonNegativeNumber = (value: any): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num >= 0;
};

/**
 * Date validation - checks if date is valid
 */
export const isValidDate = (date: any): boolean => {
  if (!date) return false;
  const d = new Date(date);
  return d instanceof Date && !isNaN(d.getTime());
};

/**
 * Date range validation - end date must be after start date
 */
export const isValidDateRange = (startDate: any, endDate: any): boolean => {
  if (!startDate || !endDate) return true; // Allow empty dates
  const start = new Date(startDate);
  const end = new Date(endDate);
  return end > start;
};

/**
 * Required field validation
 */
export const isRequired = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * Validate contract creation data
 */
export const validateContractCreation = (data: {
  supplier?: any;
  status?: string;
  contractStartDate?: any;
  contractEndDate?: any;
}): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.supplier) {
    errors.supplier = 'Supplier is required';
  }

  if (!isRequired(data.status)) {
    errors.status = 'Contract status is required';
  }

  if (data.contractStartDate && data.contractEndDate) {
    if (!isValidDateRange(data.contractStartDate, data.contractEndDate)) {
      errors.contractEndDate = 'End date must be after start date';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate utility service data
 */
export const validateUtilityService = (data: {
  serviceType?: string;
  supplier?: string;
  status?: string;
  meterDetails?: {
    meterType?: string;
    location?: string;
  };
  contractStartDate?: any;
  contractEndDate?: any;
  dayRate?: number;
  nightRate?: number;
  standingCharge?: number;
  dayConsumption?: number;
  nightConsumption?: number;
}): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!isRequired(data.serviceType)) {
    errors.serviceType = 'Service type is required';
  }

  if (!isRequired(data.supplier)) {
    errors.supplier = 'Supplier is required';
  }

  if (!isRequired(data.status)) {
    errors.status = 'Status is required';
  }

  if (!data.meterDetails?.meterType) {
    errors.meterType = 'Meter type is required';
  }

  if (!data.meterDetails?.location) {
    errors.meterLocation = 'Meter location is required';
  }

  if (data.contractStartDate && data.contractEndDate) {
    if (!isValidDateRange(data.contractStartDate, data.contractEndDate)) {
      errors.contractEndDate = 'Contract end date must be after start date';
    }
  }

  // Validate numeric fields if provided
  if (data.dayRate !== undefined && data.dayRate !== null && !isNonNegativeNumber(data.dayRate)) {
    errors.dayRate = 'Day rate must be a positive number';
  }

  if (data.nightRate !== undefined && data.nightRate !== null && !isNonNegativeNumber(data.nightRate)) {
    errors.nightRate = 'Night rate must be a positive number';
  }

  if (data.standingCharge !== undefined && data.standingCharge !== null && !isNonNegativeNumber(data.standingCharge)) {
    errors.standingCharge = 'Standing charge must be a positive number';
  }

  if (data.dayConsumption !== undefined && data.dayConsumption !== null && !isNonNegativeNumber(data.dayConsumption)) {
    errors.dayConsumption = 'Day consumption must be a positive number';
  }

  if (data.nightConsumption !== undefined && data.nightConsumption !== null && !isNonNegativeNumber(data.nightConsumption)) {
    errors.nightConsumption = 'Night consumption must be a positive number';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate client creation data
 */
export const validateClientCreation = (data: {
  businessType?: string;
  companyName?: string;
  ownerName?: string;
  email?: string;
  phoneNumber?: string;
  registeredPostcode?: string;
  tradingPostcode?: string;
}): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!isRequired(data.businessType)) {
    errors.businessType = 'Business type is required';
  }

  // Company name required for Limited Company/Charity
  if (data.businessType === 'Limited Company' || data.businessType === 'Charity') {
    if (!isRequired(data.companyName)) {
      errors.companyName = 'Company name is required';
    }
  }

  // Owner name required for Sole Trader
  if (data.businessType === 'Sole Trader') {
    if (!isRequired(data.ownerName)) {
      errors.ownerName = 'Owner name is required';
    }
  }

  // Email validation
  if (data.email && !isValidEmail(data.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Phone validation
  if (data.phoneNumber && !isValidUKPhone(data.phoneNumber)) {
    errors.phoneNumber = 'Please enter a valid UK phone number';
  }

  // Postcode validation
  if (data.registeredPostcode && !isValidUKPostcode(data.registeredPostcode)) {
    errors.registeredPostcode = 'Please enter a valid UK postcode';
  }

  if (data.tradingPostcode && !isValidUKPostcode(data.tradingPostcode)) {
    errors.tradingPostcode = 'Please enter a valid UK postcode';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validate quote creation data
 */
export const validateQuoteCreation = (data: {
  client?: any;
  property?: any;
  meterType?: string;
  meterIdentifier?: string;
  eac?: number;
  aq?: number;
  currentSupplier?: {
    supplier?: any;
    standingCharge?: number;
    dayUnitRate?: number;
  };
}): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!data.client) {
    errors.client = 'Client is required';
  }

  if (!data.property) {
    errors.property = 'Property is required';
  }

  if (!isRequired(data.meterType)) {
    errors.meterType = 'Meter type is required';
  }

  if (!isRequired(data.meterIdentifier)) {
    errors.meterIdentifier = 'Meter identifier is required';
  }

  // EAC required for electricity (MPAN)
  if (data.meterType === 'MPAN') {
    if (!data.eac || !isPositiveNumber(data.eac)) {
      errors.eac = 'EAC (Estimated Annual Consumption) is required and must be positive';
    }
  }

  // AQ required for gas (MPRN)
  if (data.meterType === 'MPRN') {
    if (!data.aq || !isPositiveNumber(data.aq)) {
      errors.aq = 'AQ (Annual Quantity) is required and must be positive';
    }
  }

  // Current supplier validation
  if (data.currentSupplier) {
    if (!data.currentSupplier.supplier) {
      errors.currentSupplier = 'Current supplier is required';
    }

    if (data.currentSupplier.standingCharge !== undefined && 
        data.currentSupplier.standingCharge !== null && 
        !isNonNegativeNumber(data.currentSupplier.standingCharge)) {
      errors.standingCharge = 'Standing charge must be a positive number';
    }

    if (data.currentSupplier.dayUnitRate !== undefined && 
        data.currentSupplier.dayUnitRate !== null && 
        !isNonNegativeNumber(data.currentSupplier.dayUnitRate)) {
      errors.dayUnitRate = 'Day unit rate must be a positive number';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors: Record<string, string>): string => {
  return Object.entries(errors)
    .map(([field, message]) => {
      // Format field name for display
      const formattedField = field
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      return `${formattedField}: ${message}`;
    })
    .join('\n');
};
